/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import studio.oddpods.cauce.core.base.designsystem.core.TopAppBarAction
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.SessionStatus
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_amount_paid
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_date
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_delete_action
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_delete_cancel
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_delete_confirm
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_delete_message
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_delete_title
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_edit_action
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_fee
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_payment_paid
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_payment_partial
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_payment_pending
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_payment_status
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_payment_waived
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_session_status
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_status_cancelled
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_status_confirmed
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_status_scheduled
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_title
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_type
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_type_double
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_type_final
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_type_first
import studio.oddpods.cauce.feature.sessions.generated.resources.session_detail_type_regular

@Composable
fun SessionDetailScreen(
    sessionId: String,
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    onEditClick: () -> Unit = {},
    viewModel: SessionDetailViewModel = koinViewModel { parametersOf(sessionId) },
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    var showDeleteDialog by remember { mutableStateOf(false) }

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            is SessionDetailEvent.SessionDeleted -> onBackClick()
        }
    }

    if (showDeleteDialog) {
        DeleteSessionDialog(
            onConfirm = {
                showDeleteDialog = false
                viewModel.trySendAction(SessionDetailAction.DeleteClick)
            },
            onDismiss = { showDeleteDialog = false },
        )
    }

    KptScaffold(
        onNavigationIconClick = onBackClick,
        title = stringResource(Res.string.session_detail_title),
        actions = listOf(
            TopAppBarAction(
                icon = Icons.Default.Edit,
                contentDescription = stringResource(Res.string.session_detail_edit_action),
                onClick = onEditClick,
            ),
            TopAppBarAction(
                icon = Icons.Default.Delete,
                contentDescription = stringResource(Res.string.session_detail_delete_action),
                onClick = { showDeleteDialog = true },
            ),
        ),
        modifier = modifier,
    ) {
        when {
            state.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
            state.error != null -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
                }
            }
            state.session != null -> {
                SessionDetailContent(state = state)
            }
        }
    }
}

@Composable
private fun SessionDetailContent(state: SessionDetailState) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        SessionInfoCard(state = state)
        SessionStatusCard(state = state)

        if (state.isDeleting) {
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        }
    }
}

@Composable
private fun SessionInfoCard(state: SessionDetailState) {
    val session = state.session!!
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            DetailRow(
                label = stringResource(Res.string.session_detail_date),
                value = state.dateDisplay,
            )
            DetailRow(
                label = stringResource(Res.string.session_detail_type),
                value = stringResource(
                    when (session.sessionType) {
                        SessionType.REGULAR -> Res.string.session_detail_type_regular
                        SessionType.FIRST -> Res.string.session_detail_type_first
                        SessionType.FINAL -> Res.string.session_detail_type_final
                        SessionType.DOUBLE -> Res.string.session_detail_type_double
                    },
                ),
            )
            DetailRow(
                label = stringResource(Res.string.session_detail_fee),
                value = state.feeDisplay,
            )
            DetailRow(
                label = stringResource(Res.string.session_detail_amount_paid),
                value = state.amountPaidDisplay,
            )
        }
    }
}

@Suppress("CyclomaticComplexMethod")
@Composable
private fun SessionStatusCard(state: SessionDetailState) {
    val session = state.session!!
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            DetailRow(
                label = stringResource(Res.string.session_detail_session_status),
                value = stringResource(
                    when (session.sessionStatus) {
                        SessionStatus.CONFIRMED -> Res.string.session_detail_status_confirmed
                        SessionStatus.CANCELLED -> Res.string.session_detail_status_cancelled
                        SessionStatus.SCHEDULED -> Res.string.session_detail_status_scheduled
                    },
                ),
                valueColor = when (session.sessionStatus) {
                    SessionStatus.CONFIRMED -> MaterialTheme.colorScheme.primary
                    SessionStatus.CANCELLED -> MaterialTheme.colorScheme.error
                    SessionStatus.SCHEDULED -> MaterialTheme.colorScheme.tertiary
                },
            )
            DetailRow(
                label = stringResource(Res.string.session_detail_payment_status),
                value = stringResource(
                    when (session.paymentStatus) {
                        PaymentStatus.PAID -> Res.string.session_detail_payment_paid
                        PaymentStatus.PENDING -> Res.string.session_detail_payment_pending
                        PaymentStatus.PARTIAL -> Res.string.session_detail_payment_partial
                        PaymentStatus.WAIVED -> Res.string.session_detail_payment_waived
                    },
                ),
                valueColor = when (session.paymentStatus) {
                    PaymentStatus.PAID -> MaterialTheme.colorScheme.primary
                    PaymentStatus.PENDING -> MaterialTheme.colorScheme.tertiary
                    PaymentStatus.PARTIAL -> MaterialTheme.colorScheme.tertiary
                    PaymentStatus.WAIVED -> MaterialTheme.colorScheme.onSurfaceVariant
                },
            )
        }
    }
}

@Composable
private fun DetailRow(
    label: String,
    value: String,
    valueColor: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.onSurface,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyLarge,
            color = valueColor,
        )
    }
}

@Composable
private fun DeleteSessionDialog(
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.session_detail_delete_title)) },
        text = { Text(stringResource(Res.string.session_detail_delete_message)) },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(
                    stringResource(Res.string.session_detail_delete_confirm),
                    color = MaterialTheme.colorScheme.error,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.session_detail_delete_cancel))
            }
        },
    )
}
